// ── DASH.B1 — Zone 1 KPI tile ───────────────────────────────────────────────
//
// Presentational only. Sizing per design doc §11: ≥ 180×120px, top RAG accent
// border, label / large value / sub-label stack. Uses the existing CSS custom
// properties (Graphite Violet tokens) — no hardcoded brand colours.

import type { CSSProperties } from 'react';
import type { RagStatus } from '../../types/dashboard';

const RAG_COLORS: Record<RagStatus, string> = {
  GREEN: 'var(--signal)',
  AMBER: 'var(--warning)',
  RED: 'var(--error)',
};

const baseTileStyle: CSSProperties = {
  minWidth: 180,
  minHeight: 120,
  flex: '1 1 180px',
  padding: 20,
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  boxShadow: 'var(--shadow-sm)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 8,
};

export interface KpiTileProps {
  label?: string;
  value?: string | number;
  subLabel?: string;
  rag?: RagStatus;
  comingSoon?: boolean;
  skeleton?: boolean;
}

export default function KpiTile({ label, value, subLabel, rag, comingSoon, skeleton }: KpiTileProps) {
  if (skeleton) {
    return (
      <div
        data-testid="kpi-tile-skeleton"
        className="animate-pulse"
        aria-hidden="true"
        style={{ ...baseTileStyle, borderTop: '1px solid var(--border)' }}
      >
        <div style={{ height: 12, width: '60%', background: 'var(--surface-alt)', borderRadius: 4 }} />
        <div style={{ height: 28, width: '40%', background: 'var(--surface-alt)', borderRadius: 4 }} />
        <div style={{ height: 12, width: '50%', background: 'var(--surface-alt)', borderRadius: 4 }} />
      </div>
    );
  }

  return (
    <div
      data-testid="kpi-tile"
      style={{
        ...baseTileStyle,
        borderTop: rag ? `3px solid ${RAG_COLORS[rag]}` : '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 30,
          fontWeight: 700,
          lineHeight: 1.1,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>

      {comingSoon ? (
        <span
          data-testid="kpi-coming-soon"
          style={{
            alignSelf: 'flex-start',
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--surface-alt)',
            color: 'var(--text-secondary)',
          }}
        >
          Coming soon
        </span>
      ) : (
        subLabel && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{subLabel}</div>
      )}
    </div>
  );
}
